// Общие примитивы админ-панели: карточки, строки, кнопки, поля, загрузчик.
//
// Зачем отдельный файл: разделов в админке стало много (пользователи, оплаты,
// тарифы, адреса, боты, поддержка, система, здоровье, аудит, бэкапы), и каждый
// из них рисует одни и те же элементы. Стили скопированы 1:1 из
// StatisticsSection/SubscriptionsSection, чтобы новые экраны выглядели
// как уже существующие (тёмные карточки, hairline-border, акцент #34D399).
//
// Тексты в разделах админки — русские, без useT(): панель видит только
// владелец (ADMIN_IDS), переводить её на английский смысла нет, а забытый
// ключ перевода показал бы «undefined» вместо подписи.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'

// ── Палитра и форматтеры ────────────────────────────────────────────────────

export const C = {
  accent: '#34D399',
  green: '#10B981',
  warn: '#F59E0B',
  indigo: '#818CF8',
  danger: '#F87171',
  red: '#FF3B30',
  muted: '#8E8E93',
  dim: '#636366',
  card: 'rgba(255,255,255,0.03)',
  line: 'rgba(255,255,255,0.06)',
  inner: 'rgba(0,0,0,0.25)',
}

export const NETWORK_LABELS = {
  TRC20: 'USDT · TRC-20',
  BEP20: 'USDT · BEP-20',
  SOL_USDT: 'USDT · SOL',
  TON: 'USDT · TON',
  ERC20: 'USDT · ERC-20',
  // Telegram Stars: журнал финансов network='stars' (план 2026-09-05)
  stars: '⭐ Telegram Stars',
}

export const fmtMoney = (x) =>
  `${Number(x || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export const fmtInt = (x) => Number(x || 0).toLocaleString('ru-RU')

// ── Время в админке: UTC+3 ──────────────────────────────────────────────────
//
// В БД ВСЕ метки лежат в UTC (CURRENT_TIMESTAMP в SQLite всегда UTC; _utc() /
// _utcStr() в TG-BOT/src/db.js — тоже; appendLog пишет toISOString()). Аналитика
// админки при этом считает бизнес-сутки по UTC+3 — см. _adminDayStart() и
// статистику в TG-BOT/src/db.js (`TZ = 3 * 3600 * 1000`). Поэтому и показываем
// UTC+3: иначе время в записи и границы «сегодня/вчера» расходятся на 3 часа
// (замечание владельца 2026-09-23: «вижу неправильное время… мой пояс UTC+3»).
//
// Смещение задано константой (не настройкой в админке и не часовым поясом
// устройства) — решение владельца. Второе такое же место — TG-BOT/src/bot.js
// (текст истории поддержки в Telegram); Node и браузер общий модуль не делят,
// поэтому при смене пояса править надо ОБА места, они ссылаются друг на друга.
export const ADMIN_TZ_OFFSET_MIN = 180

// Подпись пояса для шапок разделов — чтобы было видно, в каком времени цифры.
export const fmtTz = () => (ADMIN_TZ_OFFSET_MIN >= 0
  ? `UTC+${ADMIN_TZ_OFFSET_MIN / 60}`
  : `UTC−${Math.abs(ADMIN_TZ_OFFSET_MIN) / 60}`)

// Разбирает UTC-префикс 'YYYY-MM-DD[ T]HH:MM:SS…' и возвращает уже сдвинутый
// на ADMIN_TZ_OFFSET_MIN объект Date (читается через getUTC*). Считать надо
// именно так: new Date('2026-09-23 00:25:12') в V8 парсится как ЛОКАЛЬНОЕ время,
// поэтому «прибавить 3 часа» к текущей реализации было бы неверно.
const toAdminDate = (s) => {
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/)
  if (!m) return null
  const ms = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0))
  return new Date(ms + ADMIN_TZ_OFFSET_MIN * 60_000)
}

const p2 = (n) => String(n).padStart(2, '0')

// Даты из БД приходят как 'YYYY-MM-DD HH:MM:SS' в UTC.
export const fmtDate = (s) => {
  if (!s) return '—'
  const d = toAdminDate(s)
  return d ? `${p2(d.getUTCDate())}.${p2(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}` : String(s)
}

export const fmtDateTime = (s) => {
  if (!s) return '—'
  const d = toAdminDate(s)
  return d
    ? `${p2(d.getUTCDate())}.${p2(d.getUTCMonth() + 1)} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`
    : String(s)
}

// Дата+время целиком (для истории журналов). Пустая строка вместо '—' не нужна:
// ведёт себя как fmtDateTime.
export const fmtDateTimeFull = (s) => {
  if (!s) return '—'
  const d = toAdminDate(s)
  return d
    ? `${p2(d.getUTCDate())}.${p2(d.getUTCMonth() + 1)}.${d.getUTCFullYear()} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}:${p2(d.getUTCSeconds())}`
    : String(s)
}

// Ключ суток ('ДД.ММ.ГГГГ') по поясу админки для момента epoch-мс. Нужен там,
// где сутки надо сравнить с сегодняшними («Сегодня»/«Вчера» в журнале).
export const fmtDayKey = (ms) => {
  const d = new Date(Number(ms) + ADMIN_TZ_OFFSET_MIN * 60_000)
  return `${p2(d.getUTCDate())}.${p2(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`
}

// Часы:минуты из epoch-мс (журнал операций Telegram в карточке Stars приходит
// числами, а не строкой БД) — в том же поясе админки.
export const fmtTimeMs = (ms) => {
  if (!ms) return '—'
  const d = new Date(Number(ms) + ADMIN_TZ_OFFSET_MIN * 60_000)
  return `${p2(d.getUTCDate())}.${p2(d.getUTCMonth() + 1)}.${d.getUTCFullYear()} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`
}

// Строка api.log: appendLog (TG-BOT/src/api.js) пишет её как
// `2026-09-23T00:25:12.345Z <текст>`. Переписываем ведущую ISO-метку в
// «ДД.ММ.ГГГГ ЧЧ:ММ:СС» по поясу админки, остаток строки не трогаем.
export const fmtLogLine = (line) => {
  const s = String(line ?? '')
  const m = s.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.\d+)?Z?\s?/)
  if (!m) return s
  return `${fmtDateTimeFull(m[1].replace('T', ' '))} ${s.slice(m[0].length)}`
}

export const fmtSize = (bytes) => {
  const b = Number(bytes || 0)
  if (b >= 1024 * 1024 * 1024) return `${(b / 1073741824).toFixed(2)} ГБ`
  if (b >= 1024 * 1024) return `${(b / 1048576).toFixed(1)} МБ`
  if (b >= 1024) return `${(b / 1024).toFixed(0)} КБ`
  return `${b} Б`
}

export const fmtUptime = (sec) => {
  const s = Math.max(0, Math.floor(Number(sec || 0)))
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (d) return `${d}д ${h}ч`
  if (h) return `${h}ч ${m}м`
  return `${m}м`
}

// Короткое имя юзера для списков: username → имя → Trader<xxxx>.
export const userLabel = (u) => {
  if (!u) return '—'
  if (u.username) return `@${u.username}`
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
  if (name) return name
  return `Trader${String(u.user_id || u.userId || '').slice(-4)}`
}

// ── Стили полей ─────────────────────────────────────────────────────────────

export const inputStyle = {
  width: '100%', padding: '10px 12px', boxSizing: 'border-box',
  background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none',
  fontFamily: 'monospace',
}

export const selectStyle = {
  ...inputStyle,
  appearance: 'none', WebkitAppearance: 'none', backgroundImage: 'none',
  cursor: 'pointer',
}

export const labelStyle = { fontSize: 11, color: C.dim, marginBottom: 6, letterSpacing: '0.02em' }

// ── Компоненты ──────────────────────────────────────────────────────────────

export function Card({ title, icon: Icon, accent = C.accent, right, children, style }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.line}`, borderRadius: 16,
      padding: 16, marginBottom: 12, ...style,
    }}>
      {(title || right) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          {Icon && <Icon size={16} color={accent} strokeWidth={2.5} />}
          <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '0.01em', flex: 1 }}>
            {title}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  )
}

export function Row({ label, value, valueColor = '#fff', valueSub, mono, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        gap: 12, padding: '7px 0', cursor: onClick ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ fontSize: 12.5, color: C.muted, flexShrink: 0 }}>{label}</div>
      <div style={{ textAlign: 'right', minWidth: 0 }}>
        <div style={{
          fontSize: 13.5, fontWeight: 600, color: valueColor,
          fontFamily: mono ? 'monospace' : undefined,
          overflowWrap: 'anywhere',
        }}>{value}</div>
        {valueSub && <div style={{ fontSize: 10.5, color: C.dim, marginTop: 1 }}>{valueSub}</div>}
      </div>
    </div>
  )
}

export function BigStat({ label, value, color = C.accent, hint, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        flex: 1, minWidth: 0, background: C.inner, borderRadius: 12,
        padding: '10px 12px', cursor: onClick ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ fontSize: 10.5, color: C.dim, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color, fontFamily: 'monospace', lineHeight: 1.1 }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>{hint}</div>}
    </div>
  )
}

export function Btn({ children, onClick, color = C.accent, disabled, small, full, icon: Icon, ghost }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        width: full ? '100%' : undefined,
        padding: small ? '7px 12px' : '11px 16px',
        borderRadius: small ? 9 : 12,
        background: ghost ? 'transparent' : `${color}1F`,
        border: `1px solid ${color}${ghost ? '3D' : '52'}`,
        color: disabled ? C.dim : color,
        fontSize: small ? 12 : 13.5, fontWeight: 700,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {Icon && <Icon size={small ? 13 : 15} strokeWidth={2.5} />}
      {children}
    </button>
  )
}

export function Pill({ children, color = C.muted }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 6,
      background: `${color}1F`, color, fontSize: 10.5, fontWeight: 700,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  )
}

// Горизонтальные чипсы-фильтры (сегменты, статусы, сети).
export function Chips({ options, value, onChange }) {
  return (
    <div style={{
      display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4,
      marginBottom: 10, scrollbarWidth: 'none',
    }}>
      {options.map(o => {
        const on = o.key === value
        return (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            style={{
              flexShrink: 0, padding: '6px 11px', borderRadius: 9,
              background: on ? `${C.accent}24` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${on ? `${C.accent}5C` : C.line}`,
              color: on ? C.accent : C.muted,
              fontSize: 12, fontWeight: on ? 700 : 500,
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
          >
            {o.label}{o.count != null ? ` · ${o.count}` : ''}
          </button>
        )
      })}
    </div>
  )
}

export function Loading({ text = 'Загрузка…' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '28px 0' }}>
      <Loader2 size={16} color={C.dim} style={{ animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: 12.5, color: C.dim }}>{text}</span>
    </div>
  )
}

export function ErrorLine({ children, onRetry }) {
  if (!children) return null
  return (
    <div style={{
      background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
      borderRadius: 12, padding: '10px 12px', marginBottom: 12,
      fontSize: 12, color: C.danger, display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <span style={{ flex: 1, overflowWrap: 'anywhere' }}>{String(children)}</span>
      {onRetry && <Btn small color={C.danger} onClick={onRetry}>Ещё раз</Btn>}
    </div>
  )
}

export function Empty({ children = 'Нет данных' }) {
  return (
    <div style={{ textAlign: 'center', padding: '22px 0', fontSize: 12.5, color: C.dim }}>
      {children}
    </div>
  )
}

// Разделитель между строками списка.
export const divider = { height: 1, background: C.line, margin: '10px 0' }

// Подтверждение опасного действия (блокировка юзера, удаление тарифа, рассылка).
// В Telegram-вебвью нативный window.confirm может быть заблокирован, поэтому
// сначала пробуем tg.showConfirm (он колбэчный — оборачиваем в Promise).
export function confirmAsync(message) {
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null
  if (tg && typeof tg.showConfirm === 'function') {
    return new Promise(resolve => {
      try { tg.showConfirm(message, ok => resolve(!!ok)) }
      catch { resolve(window.confirm(message)) }
    })
  }
  return Promise.resolve(window.confirm(message))
}

// Переключатель (тумблер) — тот же, что был в AdminPanel до разделения на
// разделы: теперь его использует SystemSection и хаб (быстрый тумблер тех. работ).
export function Toggle({ on, onChange, label, hint }) {
  return (
    <button
      onClick={() => onChange(!on)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, width: '100%', padding: '12px 2px',
        background: 'transparent', border: 'none',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        cursor: 'pointer', WebkitTapHighlightColor: 'transparent', textAlign: 'left',
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: '#fff' }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: C.dim, marginTop: 2, lineHeight: 1.4 }}>{hint}</div>}
      </div>
      <div style={{
        width: 46, height: 26, borderRadius: 13, flexShrink: 0,
        background: on ? C.green : 'rgba(255,255,255,0.12)',
        position: 'relative', transition: 'background 0.2s',
      }}>
        <div style={{
          position: 'absolute', top: 3, left: on ? 23 : 3, width: 20, height: 20,
          borderRadius: '50%', background: '#fff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.4)', transition: 'left 0.2s',
        }} />
      </div>
    </button>
  )
}

// ── Хук загрузки ────────────────────────────────────────────────────────────
//
// Один и тот же паттерн во всех разделах: загрузить → показать спиннер →
// показать ошибку с кнопкой «ещё раз» → перезагрузить после действия.
// reload() возвращает Promise, чтобы кнопки могли ждать обновления.
export function useAsync(loader, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: '' })
  const ref = useRef(loader)
  ref.current = loader
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    setState(s => ({ ...s, loading: true, error: '' }))
    Promise.resolve()
      .then(() => ref.current())
      .then(d => { if (!cancelled) setState({ data: d, loading: false, error: '' }) })
      .catch(e => { if (!cancelled) setState({ data: null, loading: false, error: e?.message || String(e) }) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  const reload = useCallback(() => setTick(t => t + 1), [])
  return { ...state, reload }
}
